import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { api } from '../../api/client';
import '../../components/ui/datatable.css';
import './dashboard.css';

/* ─── Shared UI Components ─── */

export function StatusBadge({ status }: { status: string }) {
  const cfg: Record<string, { bg: string; color: string; label: string }> = {
    critico: { bg: '#fef2f2', color: '#dc2626', label: 'Crítico' },
    atencao: { bg: '#fffbeb', color: '#d97706', label: 'Atenção' },
    normal: { bg: '#f0fdf4', color: '#16a34a', label: 'Normal' },
  };
  const c = cfg[status] || cfg.normal;
  return (
    <span style={{ background: c.bg, color: c.color, padding: '3px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600 }}>
      {c.label}
    </span>
  );
}

export function KPICard({ icon: Icon, label, value, sub, color }: { icon: any; label: string; value: string | number; sub?: string; color: string }) {
  return (
    <div className="kpi-card" style={{ borderLeft: `4px solid ${color}` }}>
      <div className="kpi-card-info">
        <h3>{label}</h3>
        <span className="kpi-value">{value}</span>
        {sub && <span className="kpi-sub">{sub}</span>}
      </div>
      <div className="kpi-card-icon" style={{ background: `${color}22`, color }}>
        <Icon size={22} />
      </div>
    </div>
  );
}

export function LoadingSpinner({ label }: { label: string }) {
  return (
    <div className="admin-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 40, height: 40, border: '4px solid var(--border-color, #e5e7eb)', borderTop: '4px solid #2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
        <p style={{ color: 'var(--text-muted, #6b7280)' }}>{label}</p>
      </div>
    </div>
  );
}

export function ErrorPanel({ message }: { message?: string }) {
  return (
    <div className="admin-page">
      <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: 24, textAlign: 'center', color: '#991b1b' }}>
        <AlertTriangle size={32} style={{ margin: '0 auto 8px' }} />
        <p>{message || 'Erro ao carregar dashboard. Verifique suas permissões.'}</p>
      </div>
    </div>
  );
}

export function EntityStatusList({ items, nameKey, subLabel }: { items: any[]; nameKey: string; subLabel?: string }) {
  if (!items.length) return <p style={{ color: 'var(--text-muted, #9ca3af)', textAlign: 'center', padding: 20 }}>Nenhum registro</p>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.map((r: any, i: number) => {
        const name = r[nameKey]?.nome || r[nameKey]?.sigla || `Item ${i}`;
        return (
          <div key={r[nameKey]?.id || i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--bg-main, #f9fafb)', borderRadius: 10 }}>
            <div>
              <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>{name}</span>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted, #6b7280)', marginTop: 2 }}>
                {r.totalEscolas || 0} {subLabel || 'escolas'} · {(r.totalAlunos || 0).toLocaleString('pt-BR')} alunos · {r.taxaPresenca || 0}% presença
              </div>
            </div>
            <StatusBadge status={r.statusOperacional} />
          </div>
        );
      })}
    </div>
  );
}

export function AlertsList({ alertas }: { alertas: any[] }) {
  if (!alertas.length) return (
    <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-muted, #9ca3af)' }}>
      <CheckCircle2 size={32} style={{ margin: '0 auto 8px', color: '#16a34a' }} />
      <p>Nenhum alerta crítico ativo</p>
    </div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 300, overflowY: 'auto' }}>
      {alertas.slice(0, 10).map((a: any) => (
        <div key={a.id} style={{ padding: '10px 14px', background: a.severidade === 'CRITICA' ? '#fef2f2' : '#fffbeb', borderRadius: 10, borderLeft: `3px solid ${a.severidade === 'CRITICA' ? '#dc2626' : '#d97706'}` }}>
          <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{a.tipoIncidente?.replace(/_/g, ' ')}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted, #6b7280)', marginTop: 2 }}>
            {a.escola?.nome} · {a.aluno?.nome || 'N/A'} · {new Date(a.dataHoraReferencia).toLocaleString('pt-BR')}
          </div>
        </div>
      ))}
    </div>
  );
}

export function FeedTable({ feed }: { feed: any[] }) {
  if (!feed.length) return <p style={{ textAlign: 'center', color: 'var(--text-muted, #9ca3af)', padding: 20 }}>Nenhum evento registrado</p>;
  return (
    <div style={{ maxHeight: 280, overflowY: 'auto' }}>
      <table className="dt-table" style={{ width: '100%', fontSize: '0.82rem' }}>
        <thead><tr><th>Horário</th><th>Tipo</th><th>Aluno</th><th>Escola</th><th>Status</th></tr></thead>
        <tbody>
          {feed.slice(0, 15).map((e: any) => (
            <tr key={e.id}>
              <td>{new Date(e.dataHora).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</td>
              <td>{e.tipo === 'ENTRADA' ? '🟢 Entrada' : '🔴 Saída'}</td>
              <td>{e.aluno?.nome || '—'}</td>
              <td>{e.escola?.nome || '—'}</td>
              <td><span style={{ padding: '2px 8px', borderRadius: 8, fontSize: '0.72rem', fontWeight: 600, background: e.status === 'VALIDO' ? '#f0fdf4' : '#fef2f2', color: e.status === 'VALIDO' ? '#16a34a' : '#dc2626' }}>{e.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function StatsPanel({ title, icon: Icon, iconColor, items }: { title: string; icon: any; iconColor: string; items: { label: string; value: number | string; color?: string }[] }) {
  return (
    <div style={{ background: 'var(--bg-card, white)', borderRadius: 10, border: '1px solid var(--border-color, #e5e7eb)', padding: 14 }}>
      <h3 style={{ fontSize: '0.85rem', fontWeight: 700, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Icon size={16} style={{ color: iconColor }} /> {title}
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {items.map((item, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < items.length - 1 ? '1px solid var(--border-color, #f3f4f6)' : undefined }}>
            <span style={{ fontSize: '0.85rem' }}>{item.label}</span>
            <strong style={item.color ? { color: item.color } : undefined}>{item.value}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

export function useDashboardApi(
  key: string,
  url: string,
  options?: {
    params?: Record<string, string | undefined>;
    refetchInterval?: number | false;
  },
) {
  const params = options?.params;
  return useQuery({
    queryKey: [key, url, params],
    queryFn: async () => {
      // Usar o client axios (cookie + Bearer em impersonação/sessionStorage).
      // O fetch antigo lia só localStorage e quebrava a Portaria no simulador.
      const { data } = await api.get(url.startsWith('/') ? url : `/${url}`, {
        params: params
          ? Object.fromEntries(
              Object.entries(params).filter(
                ([, v]) => v != null && String(v).length > 0,
              ),
            )
          : undefined,
      });
      return data;
    },
    refetchInterval: options?.refetchInterval ?? 30000,
    enabled: !!url,
    refetchOnWindowFocus: true,
  });
}

export const ResponsiveStyles = () => (
  <style>{`
    @keyframes spin { to { transform: rotate(360deg); } }
    @media (max-width: 768px) {
      .kpi-grid-4 { grid-template-columns: 1fr 1fr !important; }
      div[style*="grid-template-columns: 1fr 1fr 1fr"] { grid-template-columns: 1fr !important; }
      div[style*="grid-template-columns: 1fr 1fr"] { grid-template-columns: 1fr !important; }
    }
  `}</style>
);
